import axios from "axios";
import qs from "qs";
import { client_id, client_secret, authTokenUrl } from "../util/env";
import { logRefreshToken, getRefreshToken, getAccessToken, setTokens, isAccessTokenExpired } from "../token/UserToken";

const api = axios.create({
  headers: {
    client_id: process.env.CLIENT_ID,
  },
  baseURL: process.env.API_URL,
});

// Flag para evitar múltiplas chamadas simultâneas de refresh
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      logRefreshToken('INFO', 'Token adicionado à requisição');
    }
    return config;
  },
  (error) => {
    logRefreshToken('ERROR', 'Erro no interceptor de requisição');
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    logRefreshToken('INFO', 'Resposta bem-sucedida');
    return response;
  },
  async function (error) {
    const originalRequest = error.config;
    const access_token = getAccessToken();
    const refresh_token = getRefreshToken();
    
    logRefreshToken('INFO', 'Interceptando erro de resposta');
    
    if (error.response?.status === 401 && access_token && refresh_token && !originalRequest._retry) {
      if (isRefreshing) {
        logRefreshToken('INFO', 'Refresh em andamento, adicionando à fila');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        logRefreshToken('INFO', 'Iniciando processo de refresh token');
        await refreshTokenApi();
        
        // Atualizar o token na requisição original
        const newAccessToken = getAccessToken();
        if (newAccessToken) {
          processQueue(null, newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          
          logRefreshToken('INFO', 'Refresh token concluído, repetindo requisição original');
          return api(originalRequest);
        } else {
          throw new Error('Novo token não encontrado após refresh');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        logRefreshToken('ERROR', 'Falha no refresh token, redirecionando para login');
        
        // Se falhar no refresh, fazer logout e redirecionar
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user-id");
        window.location.href = "/";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    logRefreshToken('ERROR', 'Erro não tratado pelo interceptor');
    return Promise.reject(error);
  }
);

export async function refreshTokenApi() {
  logRefreshToken('INFO', 'Iniciando refreshTokenApi');
  
  // Verificar se já está fazendo refresh
  if (isRefreshing) {
    logRefreshToken('INFO', 'Refresh já em andamento, retornando Promise existente');
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }
  
  return new Promise((resolve, reject) => {
    try {
      const refresh_token = getRefreshToken();
      
      if (!refresh_token) {
        logRefreshToken('ERROR', 'Refresh token não encontrado no localStorage');
        return reject(new Error("No refresh token available"));
      }

      // Verificar se o token atual ainda é válido (não expirado)
      const currentToken = getAccessToken();
      if (currentToken && !isAccessTokenExpired()) {
        logRefreshToken('INFO', 'Token atual ainda é válido, não é necessário refresh');
        return resolve({ data: { access_token: currentToken } });
      }

      logRefreshToken('INFO', 'Refresh token obtido do localStorage');
      isRefreshing = true;

      const data = qs.stringify({
        'client_secret': client_secret,
        'client_id': client_id,
        'refresh_token': refresh_token,
        'grant_type': 'refresh_token',
      });

      logRefreshToken('INFO', 'Enviando requisição de refresh token');

      axios
        .post(authTokenUrl(), data, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })
        .then(async (res) => {
          logRefreshToken('INFO', 'Refresh token bem-sucedido');
          
          const newAccessToken = res.data.access_token;
          const newRefreshToken = res.data.refresh_token;
          
          if (!newAccessToken || !newRefreshToken) {
            logRefreshToken('ERROR', 'Tokens não retornados na resposta do refresh');
            return reject(new Error('Tokens não retornados na resposta'));
          }

          setTokens(newAccessToken, newRefreshToken);
          logRefreshToken('INFO', 'Novos tokens salvos no localStorage');
          
          processQueue(null, newAccessToken);
          console.log("Token refreshed successfully");
          return resolve(res);
        })
        .catch((err) => {
          logRefreshToken('ERROR', 'Erro na requisição de refresh token');
          
          processQueue(err, null);
          
          // Se o refresh token também falhou, fazer logout
          if (err.response?.status === 401) {
            logRefreshToken('ERROR', 'Refresh token expirado, fazendo logout');
          }
          
          console.error("Error refreshing token:", err);
          return reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    } catch (err) {
      logRefreshToken('ERROR', 'Erro inesperado no refreshTokenApi');
      isRefreshing = false;
      return reject(err);
    }
  });
};

// async function refreshToken(error:any) {
//   return new Promise((resolve, reject) => {
//     try {
//       const refresh_token = localStorage.getItem("refresh_token");
//       const header = {
//         "Content-Type": "application/json",
//         Authorization: process.env.AUTHORIZATION,
//       };
//       const parameters = {
//         method: "POST",
//         headers: header,
//       };
//       const body = {
//         grant_type: "refresh_token",
//         refresh_token,
//       };
//       axios
//         .post(
//           process.env.API_URL + "/refreshtoken",
//           body,
//           parameters
//         )
//         .then(async (res) => {
//           localStorage.setItem("access_token", res.data.access_token);
//           localStorage.setItem("refresh_token", res.data.refresh_token);
//           // Fazer algo caso seja feito o refresh token
//           return resolve(res);
//         })
//         .catch((err) => {
//           // Fazer algo caso não seja feito o refresh token
//           return reject(error);
//         });
//     } catch (err) {
//       return reject(err);
//     }
//   });
// };

export default api;